/**
 * PhiFinds motion system — scroll reveals, parallax scenes, count-ups.
 * Every effect is gated behind prefers-reduced-motion and re-initializes
 * on Shopify theme-editor section loads.
 */
(() => {
  'use strict';

  const motionOK = () => !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Scroll reveals ---------- */
  let revealObserver;
  function initReveals(root = document) {
    const els = root.querySelectorAll('[data-reveal]:not(.is-in)');
    if (!els.length) return;
    if (!motionOK() || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    revealObserver =
      revealObserver ||
      new IntersectionObserver(
        (entries, io) => {
          for (const e of entries) {
            if (!e.isIntersecting) continue;
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        },
        { rootMargin: '0px 0px -8% 0px' }
      );
    els.forEach((el) => revealObserver.observe(el));
  }

  /* ---------- Parallax scenes ----------
     Elements with [data-parallax-speed] drift relative to the center of
     their closest [data-parallax-scene] as it crosses the viewport. */
  let parallaxEls = [];
  let scrollBound = false;
  let raf = null;

  function collectParallax() {
    parallaxEls = Array.from(document.querySelectorAll('[data-parallax-speed]')).map((el) => ({
      el,
      speed: parseFloat(el.dataset.parallaxSpeed) || 0,
      scene: el.closest('[data-parallax-scene]') || el.parentElement,
    }));
  }

  function updateParallax() {
    raf = null;
    const vh = window.innerHeight;

    /* Read every rect first, then write every style. Interleaving them forces
       a synchronous layout per element per frame, which is what makes budget
       Android scrolling stutter once a page has 10+ parallax nodes. */
    const writes = [];
    for (const { el, speed, scene } of parallaxEls) {
      const r = scene.getBoundingClientRect();
      if (r.bottom < -120 || r.top > vh + 120) continue;
      writes.push([el, (r.top + r.height / 2 - vh / 2) * speed]);
    }
    for (const [el, offset] of writes) {
      el.style.translate = `0 ${offset.toFixed(1)}px`;
    }
  }

  function initParallax() {
    collectParallax();
    if (!parallaxEls.length || !motionOK()) return;
    if (!scrollBound) {
      scrollBound = true;
      window.addEventListener(
        'scroll',
        () => {
          if (!raf) raf = requestAnimationFrame(updateParallax);
        },
        { passive: true }
      );
      window.addEventListener('resize', () => {
        if (!raf) raf = requestAnimationFrame(updateParallax);
      });
    }
    updateParallax();
  }

  /* ---------- Count-ups ---------- */
  function animateCount(el) {
    const end = parseFloat(el.dataset.count);
    if (Number.isNaN(end)) return;
    const decimals = parseInt(el.dataset.decimals || '0', 10);
    const suffix = el.dataset.suffix || '';
    const fmt = (v) => (decimals ? v.toFixed(decimals) : Math.round(v).toLocaleString('en-PK'));
    if (!motionOK()) {
      el.textContent = fmt(end) + suffix;
      return;
    }
    const t0 = performance.now();
    const duration = 1400;
    const tick = (t) => {
      const p = Math.min((t - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      el.textContent = fmt(end * eased) + (p === 1 ? suffix : '');
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  function initCounts(root = document) {
    const els = root.querySelectorAll('[data-count]:not([data-counted])');
    if (!els.length) return;
    if (!('IntersectionObserver' in window) || !motionOK()) {
      els.forEach((el) => {
        el.setAttribute('data-counted', '');
        animateCount(el);
      });
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          io.unobserve(e.target);
          e.target.setAttribute('data-counted', '');
          animateCount(e.target);
        }
      },
      { threshold: 0.6 }
    );
    els.forEach((el) => io.observe(el));
  }

  /* ---------- 3D hover tilt ----------
     [data-tilt] elements tip toward the cursor. Styling comes from the
     --tilt-x / --tilt-y custom properties (see critical.css). Optional
     strength via data-tilt="8" (degrees, default 6). */
  function initTilt() {
    if (!motionOK() || !window.matchMedia('(pointer: fine)').matches) return;
    document.querySelectorAll('[data-tilt]:not([data-tilt-init])').forEach((el) => {
      el.setAttribute('data-tilt-init', '');
      const strength = parseFloat(el.dataset.tilt) || 6;
      let raf = null;
      el.addEventListener('pointermove', (e) => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = null;
          const r = el.getBoundingClientRect();
          const x = (e.clientX - r.left) / r.width - 0.5;
          const y = (e.clientY - r.top) / r.height - 0.5;
          el.style.setProperty('--tilt-x', (-y * strength).toFixed(2) + 'deg');
          el.style.setProperty('--tilt-y', (x * strength).toFixed(2) + 'deg');
        });
      });
      el.addEventListener('pointerleave', () => {
        el.style.setProperty('--tilt-x', '0deg');
        el.style.setProperty('--tilt-y', '0deg');
      });
    });
  }

  function initAll() {
    initReveals();
    initParallax();
    initCounts();
    initTilt();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Theme editor: sections re-render in place */
  document.addEventListener('shopify:section:load', initAll);

  /* Dynamically injected content (e.g. fetched recommendations) */
  document.addEventListener('phi:content-loaded', initAll);
  window.PhiMotion = { refresh: initAll };
})();
